import { doc, getDoc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { db } from '@/config/firebase';

export const UPLOAD_BATCH_STATUS_VALUES = {
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

export type UploadBatchStatus =
  (typeof UPLOAD_BATCH_STATUS_VALUES)[keyof typeof UPLOAD_BATCH_STATUS_VALUES];

export interface FirebaseUploadBatchData {
  batchId: string;
  documentId: string;
  status: UploadBatchStatus;
  totalCount: number;
  processedCount?: number;
  error?: string;
  created_at?: string;
  updated_at?: string;
}

export const listenToUploadBatchStatus = (
  documentId: string,
  onStatusChange: (data: FirebaseUploadBatchData) => void,
  onError?: (error: Error) => void,
  collectionName: string = 'screenshot_batches'
): Unsubscribe => {
  const docRef = doc(db, collectionName, documentId);

  const unsubscribe = onSnapshot(
    docRef,
    (doc) => {
      if (doc.exists()) {
        const rawData = doc.data();

        // Convert snake_case to camelCase
        const data: FirebaseUploadBatchData = {
          batchId: rawData.batch_id || rawData.batchId || '',
          documentId: rawData.document_id || rawData.documentId || documentId,
          status: rawData.status as UploadBatchStatus,
          totalCount: rawData.total_count || rawData.totalCount || 0,
          processedCount:
            rawData.processed_count || rawData.processedCount || 0,
          error: rawData.error,
          created_at: rawData.created_at,
          updated_at: rawData.updated_at,
        };

        onStatusChange(data);
      }
    },
    (error) => {
      onError?.(error);
    }
  );

  return unsubscribe;
};

export const getUploadedImageUrls = async (
  documentId: string,
  collectionName: string = 'screenshot_batches'
): Promise<string[]> => {
  const docRef = doc(db, collectionName, documentId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error(`Batch document ${documentId} not found`);
  }

  const data = docSnap.data();

  // Backend stores URLs in 'image_urls' array
  if (data.image_urls && Array.isArray(data.image_urls)) {
    return data.image_urls;
  }

  // Fallback: Check for results array with image_url field
  if (data.results && Array.isArray(data.results)) {
    const urls = data.results
      .map((result: { image_url?: string }) => result.image_url)
      .filter((url): url is string => typeof url === 'string');
    return urls;
  }

  return [];
};
